// Path: ./src/app/pages/CreateUploadFlyer/index.tsx
import {
  Container,
  CardWrapper,
  Card,
  BottomSection,
  ButtonWrapper,
  LeftSideWrapper,
  RightSideWrapper,
  RightSideTitle,
  InheritTextInputField,
  DotDotDivider,
  UnitWrapper,
  SubmitButton,
  TopSection,
  LeftSideTitle,
  SmallImageWrapper,
  FlyerImageContainer,
  RankWrapper,
  RankTitle,
  RankInputField,
  DateSection,
  DateButtonWrapper,
  DateButton,
  UploadImageWrapper,
  CustomUploadLabelCard,
  DragElement,
  DragInputElement,
} from "./styled";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { SmallDealImgCard } from "../../components/SmallDealImgCard";
import { CustomAutocompleteMultiSelect } from "../../components/CustomAutocomplete";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ChangeEvent, useEffect, useState } from "react";

// Configure dayjs to use timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
import {
  GfFlyerTag,
  GfImage,
  GfImageDto,
  GfMerchant,
  GfStore,
  UpdateGfFlyerDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../utils/api";
import { DateRange } from "react-date-range";
import { selectShortCutDateRangeFn } from "../../../utils/SelectRangeDates";
import AddIcon from "@mui/icons-material/Add";

import { useLoading } from "../../../contextApi/LoadingContext";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { useMerchants } from "../../../hooks/useMerchants";
import { useFlyerTags } from "../../../hooks/useFlyerTags";
import { useStores } from "../../../hooks/useStores";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";
import { CircularProgress, Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useHandleSaveFlyer } from "./handleSaveFlyer";
// @ts-ignore - allow side-effect CSS import without type declarations
import "./styles.css";
import { disableWheelOnInput } from "../../../utils/disableWheelOnInput";
import { ImageDropZone } from "../../../components/ImageDropZone";
// import { setTimeTo1201AM } from "../../../utils/dateUtil/setTimeTo1201AM";

export const CreateUploadFlyer = () => {
  const { flyerId } = useParams();
  const { showLoadingScreen } = useLoading();
  const { t } = useTranslation();
  const handleSaveFlyer = useHandleSaveFlyer();
  const {
    merchantsList,
    isMerchantOptionLoading,

    handleOnMerchantSearch,
  } = useMerchants();
  const { flyerTagsList } = useFlyerTags();
  const { storesList, isStoreOptionLoading, handleOnStoreSearch } = useStores();

  const [updateUploadFlyer, setUpdateUploadFlyer] =
    useState<UpdateGfFlyerDto>();
  const [initialPickedImages, setInitialPickedImages] = useState<GfImageDto[]>(
    [],
  );
  const [dateButtonSelected, setDateButtonSelected] = useState(0);
  const [publishDate, setPublishDate] = useState<Dayjs | null>(
    dayjs().tz("America/New_York"),
  );
  const [areTagsAutoFilled, setAreTagsAutoFilled] = useState(false);

  const {
    isUploadingImage,
    handleOnImageFileSelect,
    handleOnRemoveImage,
    isDragging,
    getDropZoneProps,
    showReorderModal,
    pendingDropFiles,
    handleReorderConfirm,
    handleReorderCancel,
  } = useUploadOrPickImages({
    onPickedImageChange: (images: GfImage[]) => {
      setUpdateUploadFlyer((prev) => {
        return {
          ...prev,
          gfImages: images,
        };
      });
    },
    initialPickedImages,
    dragAndDrop: { enabled: true, defaultPath: "flyers" },
  });

  /**
   * Fetches last used flyer tags for a given store or merchant.
   * Used to auto-fill flyer tags in create mode.
   * @param storeId - Optional store ID to fetch tags for
   * @param merchantId - Optional merchant ID to fetch tags for
   * @returns Promise<GfFlyerTag[]> - Array of flyer tags or empty array on error
   */
  const fetchLastFlyerTags = async (
    storeId?: string,
    merchantId?: string,
  ): Promise<GfFlyerTag[]> => {
    try {
      const tags =
        await adminApiService.gfFlyerAdminControllerFindLastFlyerTagByStoreId(
          storeId,
          merchantId,
        );
      return tags || [];
    } catch (error) {
      console.error("Error fetching last flyer tags:", error);
      return [];
    }
  };

  /**
   * Handles merchant selection change.
   *
   * Behavior:
   * - Clears stores when a merchant is selected (merchant and stores are mutually exclusive)
   * - Updates the selected merchant
   * - Clears auto-filled tags when merchant is cleared/deselected
   * - Auto-fills tags from last used flyer tags when merchant is selected
   *
   * Edge cases handled:
   * 1. Edit mode: Auto-fill is disabled when editing existing flyer (flyerId exists or updateUploadFlyer.id exists)
   * 2. Manual tag selection: Auto-fill is skipped if user already selected tags manually
   * 3. Tag clearing: Only auto-filled tags are cleared when merchant is deselected, manual selections are preserved
   * 4. Empty merchant: Safely handles undefined/null merchant selection
   * 5. API failure: Gracefully handles tag fetch errors (returns empty array)
   * 6. No tags returned: Does not update state if API returns empty array
   *
   * @param merchants - Array of selected merchants (single merchant expected due to multiSelect=false)
   */
  const handleMerchantChange = async (merchants: GfMerchant[] | undefined) => {
    const selectedMerchant = merchants?.[0];

    // Clear auto-filled tags if merchant is cleared/changed
    const shouldClearTags = !selectedMerchant && areTagsAutoFilled;

    setUpdateUploadFlyer((prev) => {
      return {
        ...prev,
        stores: undefined,
        merchant: selectedMerchant,
        ...(shouldClearTags && { flyerTag: undefined }),
      };
    });

    if (shouldClearTags) {
      setAreTagsAutoFilled(false);
    }

    // Auto-fill flyer tags only in create mode and if no tags exist
    const isCreateMode = !flyerId && !updateUploadFlyer?.id;
    const hasNoExistingTags =
      !updateUploadFlyer?.flyerTag || updateUploadFlyer.flyerTag.length === 0;

    if (isCreateMode && hasNoExistingTags && selectedMerchant?.id) {
      const tags = await fetchLastFlyerTags(undefined, selectedMerchant.id);
      if (tags.length > 0) {
        setUpdateUploadFlyer((prev) => ({
          ...prev,
          flyerTag: tags,
        }));
        setAreTagsAutoFilled(true);
      }
    }
  };

  /**
   * Handles store selection change.
   *
   * Behavior:
   * - Updates the selected stores (multi-select enabled)
   * - Clears auto-filled tags when all stores are cleared/deselected
   * - Auto-fills tags from last used flyer tags when stores are selected
   *
   * Edge cases handled:
   * 1. Edit mode: Auto-fill is disabled when editing existing flyer (flyerId exists or updateUploadFlyer.id exists)
   * 2. Manual tag selection: Auto-fill is skipped if user already selected tags manually
   * 3. Tag clearing: Only auto-filled tags are cleared when stores are deselected, manual selections are preserved
   * 4. Empty stores: Safely handles undefined/null/empty array store selection
   * 5. Multiple stores: Uses first store's ID for tag lookup when multiple stores are selected
   * 6. API failure: Gracefully handles tag fetch errors (returns empty array)
   * 7. No tags returned: Does not update state if API returns empty array
   * 8. Store without ID: Skips auto-fill if store object doesn't have a valid ID
   *
   * @param stores - Array of selected stores (multi-select enabled)
   */
  const handleStoreChange = async (stores: GfStore[] | undefined) => {
    // Clear auto-filled tags if stores are cleared
    const shouldClearTags =
      (!stores || stores.length === 0) && areTagsAutoFilled;

    setUpdateUploadFlyer({
      ...updateUploadFlyer,
      stores: stores,
      ...(shouldClearTags && { flyerTag: undefined }),
    });

    if (shouldClearTags) {
      setAreTagsAutoFilled(false);
    }

    if (stores && stores.length > 0) {
      // Auto-fill flyer tags only in create mode and if no tags exist
      const isCreateMode = !flyerId && !updateUploadFlyer?.id;
      const hasNoExistingTags =
        !updateUploadFlyer?.flyerTag || updateUploadFlyer.flyerTag.length === 0;

      // Use first store's ID if multiple stores selected
      if (isCreateMode && hasNoExistingTags && stores[0]?.id) {
        const tags = await fetchLastFlyerTags(stores[0].id, undefined);
        if (tags.length > 0) {
          setUpdateUploadFlyer((prev) => ({
            ...prev,
            flyerTag: tags,
          }));
          setAreTagsAutoFilled(true);
        }
      }
    }
  };

  /**
   * Handles button click event and updates date range for creating a flyer.
   * It's a ShortCut Button Function where we can select these 3 options
   * 1. today only, 2. fri-sun and 3.Mon-thur
   * @param button - The button identifier indicating the selected date shortcut.
   */
  const handleButtonClick = (button: number) => {
    const rangeDate = selectShortCutDateRangeFn(new Date(), button);
    setDateButtonSelected(button);
    if (rangeDate?.length) {
      setUpdateUploadFlyer({
        ...updateUploadFlyer,
        validStartDate: rangeDate[0],
        validEndDate: rangeDate[1],
      });
    }
  };

  const initializeData = async () => {
    showLoadingScreen(true);
    if (flyerId) {
      const flyer =
        await adminApiService.getOneBaseGfFlyerAdminControllerGfFlyer(flyerId);
      /**
       * this is might due to sdk issue. so even sdk said validStartDate and validEndDate is Date data type, but when we fetch from server, sdk didn't convert it to Date but still string. I think we need to do this in the api.ts file not here. so temp solution for now
       */
      flyer.validStartDate = new Date(flyer.validStartDate);
      flyer.validEndDate = new Date(flyer.validEndDate);
      setUpdateUploadFlyer(flyer);
      setInitialPickedImages(flyer?.gfImages || []);
      if (flyer.publishDate) {
        setPublishDate(dayjs(flyer.publishDate).tz("America/New_York"));
      }
    }
    showLoadingScreen(false);
  };

  useEffect(() => {
    initializeData();
  }, []);

  return (
    <Container data-testid="createuploadflyer-test-id">
        <CardWrapper>
          <Card>
            <LeftSideWrapper>
              <TopSection>
                <LeftSideTitle>{t(messages.Name())}</LeftSideTitle>
                <InheritTextInputField
                  placeholder={t(messages.Name()) as string}
                  onChange={(event) => {
                    setUpdateUploadFlyer({
                      ...updateUploadFlyer,
                      pdf: event.target.value,
                    });
                  }}
                  value={updateUploadFlyer?.pdf}
                  data-testid={`first-name-input`}
                ></InheritTextInputField>

                <CustomAutocompleteMultiSelect
                  title={`${t(messages.Merchant())}`}
                  isLoading={isMerchantOptionLoading}
                  selectedValues={
                    updateUploadFlyer?.merchant
                      ? [updateUploadFlyer?.merchant].map((merchant) => ({
                          value: merchant,
                          label: merchant?.name,
                        }))
                      : []
                  }
                  handleOnChange={handleMerchantChange}
                  optionsList={merchantsList?.map((merchant) => ({
                    value: merchant,
                    label: merchant.name,
                  }))}
                  handleOnSearch={handleOnMerchantSearch}
                  multiSelect={false}
                  WrapperCss={`
              margin-left:10px;
              margin-top:10px;
              width:100%;
              max-width: 664px;
            `}
                  inputCss={`
            width:100%;
            max-width: 664px;
            `}
                  inputTextCss={`width:100%;max-width: 664px;`}
                  dataTestId={`merchant-select`}
                ></CustomAutocompleteMultiSelect>

                {updateUploadFlyer?.merchant ? (
                  <></>
                ) : (
                  <CustomAutocompleteMultiSelect
                    title={t(messages.Stores()) as string}
                    optionsList={storesList?.map((store) => ({
                      value: store,
                      label: makeStoreDisplayName(store),
                    }))}
                    isLoading={isStoreOptionLoading}
                    selectedValues={updateUploadFlyer?.stores?.map((store) => ({
                      value: store,
                      label: makeStoreDisplayName(store),
                    }))}
                    multiSelect={true}
                    handleOnChange={handleStoreChange}
                    handleOnSearch={handleOnStoreSearch}
                    WrapperCss={`
              margin-left:10px;
              margin-top:10px;
              width:100%;
              max-width: 664px;
            `}
                    inputCss={`
            width:100%;
            max-width: 664px;
            `}
                    dataTestId={`store-select`}
                  />
                )}
              </TopSection>
              <DotDotDivider></DotDotDivider>

              <UnitWrapper>
                <CustomAutocompleteMultiSelect
                  title={`${t(messages.Flyer_Tag())}`}
                  optionsList={flyerTagsList?.map((tag) => ({
                    value: tag,
                    label: tag.name,
                  }))}
                  selectedValues={updateUploadFlyer?.flyerTag?.map((tag) => ({
                    value: tag,
                    label: tag.name,
                  }))}
                  multiSelect={true}
                  handleOnChange={(value: GfFlyerTag[] | [] | undefined) => {
                    setUpdateUploadFlyer({
                      ...updateUploadFlyer,
                      flyerTag: value,
                    });
                    setAreTagsAutoFilled(false);
                  }}
                  inputCss={`
                    width: 18.75rem;
                `}
                  dataTestId={`Tag-select`}
                />
                <RankWrapper>
                  <RankTitle>{t(messages.Rank())}</RankTitle>
                  <RankInputField
                    placeholder={t(messages.Rank()) as string}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setUpdateUploadFlyer({
                        ...updateUploadFlyer,
                        rank: parseInt(event.target.value),
                      });
                    }}
                    value={updateUploadFlyer?.rank}
                    type="number"
                    onWheel={disableWheelOnInput}
                    data-testid={`rank-input`}
                  ></RankInputField>
                </RankWrapper>
              </UnitWrapper>
              <DotDotDivider></DotDotDivider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <div
                  style={{
                    marginLeft: "10px",
                    marginTop: "20px",
                    marginBottom: "20px",
                    width: "100%",
                    maxWidth: "664px",
                  }}
                >
                  <div
                    style={{
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#333",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {t(messages.Publish_Date_and_Time())} (New York Time -
                    EST/EDT)
                    <Tooltip
                      title={t(messages.Publish_Date_and_Time_Tooltip())}
                      arrow
                      placement="top"
                      sx={{
                        "& .MuiTooltip-tooltip": {
                          backgroundColor: "#333",
                          color: "#fff",
                          fontSize: "13px",
                          padding: "10px 14px",
                          borderRadius: "8px",
                          maxWidth: "300px",
                          lineHeight: "1.5",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        },
                        "& .MuiTooltip-arrow": {
                          color: "#333",
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          padding: "2px",
                          color: "grey",
                          "&:hover": {
                            backgroundColor: "rgba(255, 128, 56, 0.08)",
                          },
                        }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <DateTimePicker
                    value={publishDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setPublishDate(newValue.tz("America/New_York"));
                      } else {
                        setPublishDate(null);
                      }
                    }}
                    timezone="America/New_York"
                    timeSteps={{ hours: 1, minutes: 1 }}
                    views={["year", "month", "day", "hours", "minutes"]}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: "Select publish date and time (NY Time)",
                        inputProps: {
                          disabled: true,
                        },
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            cursor: "pointer",

                            "&.Mui-focused fieldset": {
                              borderColor: "#ff8038",
                              borderWidth: "2px",
                            },
                            "& fieldset": {
                              borderColor: "#e0e0e0",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#ff8038",
                          },
                          "& input": {
                            cursor: "pointer",
                            userSelect: "none",
                            fontWeight: "500",
                            color: "#333",
                          },
                          "& .MuiInputAdornment-root .MuiIconButton-root": {
                            color: "#ff8038",
                          },
                        },
                      },
                      popper: {
                        sx: {
                          "& .MuiPaper-root": {
                            borderRadius: "12px",
                            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                            marginTop: "8px",
                          },
                          "& .MuiPickersDay-root": {
                            borderRadius: "8px",
                            fontWeight: "500",
                            "&:hover": {
                              backgroundColor: "rgba(255, 128, 56, 0.12)",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#ff8038",
                              color: "#fff",
                              fontWeight: "600",
                              "&:hover": {
                                backgroundColor: "#e67332",
                              },
                              "&:focus": {
                                backgroundColor: "#ff8038",
                              },
                            },
                            "&.MuiPickersDay-today": {
                              border: "2px solid #ff8038",
                              backgroundColor: "transparent",
                              "&.Mui-selected": {
                                backgroundColor: "#ff8038",
                                border: "2px solid #ff8038",
                              },
                            },
                          },
                          "& .MuiPickersCalendarHeader-root": {
                            paddingTop: "16px",
                            paddingBottom: "8px",
                            "& .MuiPickersCalendarHeader-label": {
                              color: "#ff8038",
                              fontWeight: "600",
                              fontSize: "16px",
                            },
                            "& .MuiIconButton-root": {
                              color: "#ff8038",
                              "&:hover": {
                                backgroundColor: "rgba(255, 128, 56, 0.08)",
                              },
                            },
                          },
                          "& .MuiPickersYear-yearButton": {
                            fontSize: "14px",
                            "&.Mui-selected": {
                              backgroundColor: "#ff8038",
                              color: "#fff",
                              fontWeight: "600",
                              "&:hover": {
                                backgroundColor: "#e67332",
                              },
                            },
                            "&:hover": {
                              backgroundColor: "rgba(255, 128, 56, 0.12)",
                            },
                          },
                          "& .MuiClock-pin": {
                            backgroundColor: "#ff8038",
                          },
                          "& .MuiClockPointer-root": {
                            backgroundColor: "#ff8038",
                          },
                          "& .MuiClockPointer-thumb": {
                            backgroundColor: "#ff8038",
                            border: "2px solid #fff",
                          },
                          "& .MuiClockNumber-root": {
                            "&.Mui-selected": {
                              backgroundColor: "#ff8038",
                              color: "#fff",
                              fontWeight: "600",
                            },
                          },
                          "& .MuiPickersLayout-actionBar .MuiButton-root": {
                            color: "#ff8038",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            fontSize: "13px",
                            "&:hover": {
                              backgroundColor: "rgba(255, 128, 56, 0.08)",
                            },
                          },
                          "& .MuiMultiSectionDigitalClock-root": {
                            "& .MuiMenuItem-root": {
                              "&.Mui-selected": {
                                backgroundColor: "#ff8038",
                                color: "#fff",
                                fontWeight: "600",
                                "&:hover": {
                                  backgroundColor: "#e67332",
                                },
                              },
                              "&:hover": {
                                backgroundColor: "rgba(255, 128, 56, 0.12)",
                              },
                            },
                          },
                          "& .MuiTimeClock-root": {
                            "& .MuiClockNumber-root": {
                              fontWeight: "500",
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </LocalizationProvider>

              <DateSection>
                <DateButtonWrapper>
                  <DateButton
                    onClick={() => {
                      handleButtonClick(1);
                    }}
                    isSelected={dateButtonSelected === 1}
                  >
                    {t(messages.Today_Only())}
                  </DateButton>
                  <DateButton
                    data-testid="date-fri-sun-button"
                    isSelected={dateButtonSelected === 2}
                    onClick={() => {
                      handleButtonClick(2);
                    }}
                  >
                    {t(messages.Fri_Sun())}
                  </DateButton>
                  <DateButton
                    isSelected={dateButtonSelected === 3}
                    onClick={() => {
                      handleButtonClick(3);
                    }}
                  >
                    {t(messages.Mon_Thur())}
                  </DateButton>
                  <DateButton
                    isSelected={dateButtonSelected === 4}
                    onClick={() => {
                      handleButtonClick(4);
                    }}
                  >
                    {t(messages.Thur_Fri())}
                  </DateButton>
                </DateButtonWrapper>
                <DateRange
                  ranges={[
                    {
                      startDate:
                        updateUploadFlyer && updateUploadFlyer.validStartDate
                          ? new Date(updateUploadFlyer.validStartDate) //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) not sure why we need to do new Date again since validStartDate is already Date data type, will come back later
                          : new Date(),
                      endDate:
                        updateUploadFlyer && updateUploadFlyer?.validEndDate
                          ? new Date(updateUploadFlyer.validEndDate)
                          : new Date(),
                      key: "selection",
                    },
                  ]}
                  months={2}
                  moveRangeOnFirstSelection={false}
                  showPreview={true}
                  showDateDisplay={false}
                  rangeColors={["#EF8200"]}
                  displayMode="dateRange"
                  direction="horizontal"
                  color="#EF8200"
                  onChange={(item) => {
                    setUpdateUploadFlyer({
                      ...updateUploadFlyer,
                      validStartDate: item.selection.startDate,
                      validEndDate: item.selection.endDate,
                    });
                  }}
                />
              </DateSection>
            </LeftSideWrapper>
            <RightSideWrapper>
              <RightSideTitle>
                {t(messages.Select_Flyer_or_Drop_Flyer_Here())}
              </RightSideTitle>
              <ButtonWrapper>
                <ImageDropZone
                  uploader={{
                    getDropZoneProps,
                    isDragging,
                    showReorderModal,
                    pendingDropFiles,
                    handleReorderConfirm,
                    handleReorderCancel,
                  }}
                  label={t(messages.Drag_your_image_here())}
                  overlayContent={t(messages.Drag_your_image_here())}
                  overlayStyles={{
                    background: "rgba(0,0,0,0.4)",
                    color: "#fff",
                    fontSize: 18,
                    fontWeight: 600,
                    border: "2px dashed #ff813a",
                  }}
                  style={{ position: "relative", width: "100%" }}
                  data-testid="drag-drop-zone"
                >
                  <DragElement>
                    <DragInputElement
                      type="file"
                      id="upload"
                      accept="image/*"
                      multiple
                      onChange={handleOnImageFileSelect}
                      data-testid={`image-local-upload`}
                    />
                    {isUploadingImage ? (
                      <CircularProgress sx={{ color: "#ff813a" }} size={150} />
                    ) : (
                      <CustomUploadLabelCard htmlFor="upload">
                        <AddIcon style={{ fontSize: 40 }} />
                      </CustomUploadLabelCard>
                    )}
                  </DragElement>{" "}
                </ImageDropZone>
              </ButtonWrapper>
              <UploadImageWrapper>
                <FlyerImageContainer>
                  {updateUploadFlyer?.gfImages?.length ? (
                    <>
                      {updateUploadFlyer?.gfImages?.map(
                        (value: GfImage, index: string | number) => {
                          return (
                            <SmallImageWrapper
                              key={index}
                              data-testid="createuploadflyer-test-id"
                            >
                              <SmallDealImgCard
                                imageSrc={
                                  value?.src
                                  //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) Syed please fix this
                                  // ? URL.createObjectURL(value.file)
                                  // : value.src
                                }
                                cancelButtonClick={() => {
                                  handleOnRemoveImage &&
                                    handleOnRemoveImage(value.id);
                                }}
                              />
                            </SmallImageWrapper>
                          );
                        },
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </FlyerImageContainer>
              </UploadImageWrapper>

              <BottomSection></BottomSection>
              <SubmitButton
                onClick={async () => {
                  const flyerDataWithPublishDate = {
                    ...updateUploadFlyer,
                    publishDate: publishDate?.toDate(),
                  };
                  await handleSaveFlyer(flyerDataWithPublishDate);
                }}
                data-testid={`submit-button`}
              >
                {updateUploadFlyer?.id
                  ? t(messages.Update())
                  : t(messages.Save())}
              </SubmitButton>
            </RightSideWrapper>
          </Card>
        </CardWrapper>
      </Container>
  );
};
